import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { isMobile } from 'utils/is-mobile';
import { filterSportDataByGameStartTsRange } from 'utils/sportsbook/filterSportDataByGameStartTsRange';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport } from 'interfaces/sportsbook-data-levels';
import { TopLeagueCompactMenuItem } from 'elements/SportsbookLeftSideBar/partials/TopLeagueCompactMenuItem';
import { AdvancedRangeFilter } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedCompetitionsBySportLayout/AdvancedRangeFilter';
import { ADVANCED_LEFT_MENU_DATA_REDUX_KEY } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedLeftSideBar/AdvancedLeftMenu';
import {
  AdvancedSportsContent,
  EShowEventsButtonLayout
} from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedSportsContent';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { NoItem } from 'components/Shared/NoItem';
import { RegionSkeleton } from 'components/UI/Skeletons/RegionSkeleton';
import {
  getAdvancedSelectedRangeFilter,
  getSportsbookSelectedSport,
  getSwarmResponse
} from 'store/selectors/new-sportsbook';
import './index.less';

export const ADVANCED_SPORT_BY_COMPETITIONS_LAYOUT_SHOW_EVENTS_BUTTON_ID =
  'advancedSportByCompetitionsShowEvents';

export const AdvancedCompetitionsBySportLayout = () => {
  const { t } = useTranslation();

  const mainSportsMenuSelectedSport = useSelector(getSportsbookSelectedSport);

  const sportsData =
    useSelector(getSwarmResponse)?.[ADVANCED_LEFT_MENU_DATA_REDUX_KEY];

  const selectedRange = useSelector(getAdvancedSelectedRangeFilter);
  const topLeaguesData =
    useSelector(getSwarmResponse)?.[ENotRealSports.topLeague];

  const filteredSportData: Sport | undefined = useMemo(() => {
    const dataBySport =
      mainSportsMenuSelectedSport?.id &&
      sportsData?.[mainSportsMenuSelectedSport?.id];

    if (dataBySport) {
      if (!selectedRange?.ranges) {
        return dataBySport;
      } else {
        const ranges = selectedRange.ranges;
        const filteredData = filterSportDataByGameStartTsRange(
          { [mainSportsMenuSelectedSport.id]: dataBySport },
          ranges
        );

        if (filteredData && Object.keys(filteredData).length === 0) {
          return {} as Sport;
        }

        return filteredData[mainSportsMenuSelectedSport.id];
      }
    }
  }, [mainSportsMenuSelectedSport?.id, sportsData, selectedRange]);

  const topLeaguesFilteredData = useMemo(() => {
    if (topLeaguesData) {
      let filteredData = { ...topLeaguesData };

      if (selectedRange?.ranges) {
        filteredData = filterSportDataByGameStartTsRange(
          topLeaguesData,
          selectedRange?.ranges
        );
      }

      return filteredData;
    }
  }, [topLeaguesData, selectedRange?.ranges]);

  const noCompetitionsAvailable =
    filteredSportData && Object.keys(filteredSportData).length === 0;

  return (
    <div
      className={cc([
        'advancedSportByCompetitionsLayoutWrapper',
        {
          'advancedSportByCompetitionsLayoutWrapper--mobile': isMobile(),
          'advancedSportByCompetitionsLayoutWrapper--fullHeight':
            noCompetitionsAvailable
        }
      ])}
    >
      <AdvancedRangeFilter selectedRange={selectedRange} />

      {topLeaguesFilteredData &&
      Object.keys(topLeaguesFilteredData).length === 0 ? null : (
        <div className="advancedSportByCompetitionsLayout">
          <NavigationCollapse
            key="topLeague"
            defaultActive
            wrapperClassName="advancedByCompetitionWrapper"
            headerClassName="advancedByCompetitionHeader"
            title={t('sportsbook.topLeagues')}
          >
            <TopLeagueCompactMenuItem
              showCollapse={false}
              sportIconSize={20}
              regionIconSize={20}
              externalDataSource={topLeaguesFilteredData}
            />
          </NavigationCollapse>
        </div>
      )}

      <div
        className={cc([
          'advancedSportByCompetitionsLayout',
          {
            'advancedSportByCompetitionsLayout--fullHeight':
              noCompetitionsAvailable
          }
        ])}
      >
        <NavigationCollapse
          key="competitions"
          defaultActive
          wrapperClassName={cc([
            'advancedByCompetitionWrapper',
            {
              'advancedByCompetitionWrapper--fullHeight':
                noCompetitionsAvailable
            }
          ])}
          headerClassName="advancedByCompetitionHeader"
          title={t('sportsbook.competitions')}
        >
          {filteredSportData ? (
            noCompetitionsAvailable ? (
              <NoItem
                className="advancedNoCompetitions"
                size="medium"
                title={t('sportsbook.noCompetition')}
                text={t('sportsbook.noCompetitionText')}
              />
            ) : (
              <AdvancedSportsContent
                sportsData={[filteredSportData]}
                showSportCollapse={false}
                portalRootId={
                  ADVANCED_SPORT_BY_COMPETITIONS_LAYOUT_SHOW_EVENTS_BUTTON_ID
                }
                showEventsButtonType={
                  isMobile()
                    ? EShowEventsButtonLayout.BUTTON
                    : EShowEventsButtonLayout.UNDERLINE
                }
                showAllEventsExtraClassname="advancedSportByCompetitionsLayoutShowAllEvents"
              />
            )
          ) : (
            <RegionSkeleton />
          )}
        </NavigationCollapse>
      </div>
    </div>
  );
};
